import { ValidationResult, FILE_VALIDATION } from '../types';

/**
 * Validate a file for upload
 */
export const validateFile = (file: File): ValidationResult => {
    const errors: ValidationResult['errors'] = [];

    // Check file size
    if (file.size > FILE_VALIDATION.maxFileSize) {
        errors.push({
            code: 'FILE_TOO_LARGE',
            message: `File melebihi batas ${formatFileSize(FILE_VALIDATION.maxFileSize)}`,
            field: 'size',
        });
    }

    // Check file extension
    const extension = getFileExtension(file.name);
    if (!(FILE_VALIDATION.acceptedExtensions as readonly string[]).includes(extension)) {
        errors.push({
            code: 'INVALID_FORMAT',
            message: `Format ${extension || 'tidak dikenal'} tidak didukung. Gunakan ${FILE_VALIDATION.acceptedExtensions.join(', ')}`,
            field: 'type',
        });
    }

    return {
        valid: errors.length === 0,
        errors,
    };
};

/**
 * Get file extension including the dot
 */
export const getFileExtension = (filename: string): string => {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot === -1) return '';
    return filename.substring(lastDot).toLowerCase();
};

/**
 * Format file size to human readable string
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Get file type icon based on extension
 */
export const getFileTypeIcon = (filename: string): string => {
    const ext = getFileExtension(filename);
    switch (ext) {
        case '.zip':
            return 'FolderZip';
        case '.shp':
            return 'Map';
        case '.gpkg':
            return 'Database';
        default:
            return 'InsertDriveFile';
    }
};

/**
 * Validate shapefile ZIP contents
 * A valid shapefile ZIP should contain .shp, .shx, .dbf files
 */
export const validateShapefileZip = async (file: File): Promise<ValidationResult> => {
    // For now, just return valid - actual ZIP inspection would require a library
    // This is a placeholder for future implementation
    const basicValidation = validateFile(file);
    if (!basicValidation.valid) {
        return basicValidation;
    }

    // TODO: Implement ZIP content inspection
    // For mock purposes, assume valid
    return { valid: true, errors: [] };
};

/**
 * Check if file is a spatial file
 */
export const isSpatialFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return (FILE_VALIDATION.acceptedExtensions as readonly string[]).includes(ext);
};
